import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import {
  canContinueWithNewSocialAccount,
  getSocialCandidateEmails,
  getSocialLoginErrorMessage,
  getSocialSessionToken,
  isSocialLoginSuccess,
  shouldRequireEmailVerification,
  socialLogin
} from '../data/repositories/SocialLogin';
import { completeAuthLogin } from '@pocapan/shared/utils/completeAuthLogin';
import { getGoogleRedirectUri } from '@pocapan/shared/utils/socialAuthWeb';
import GradientLoadingSpinner from '../presentation/components/GradientLoadingSpinner';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';

const OAUTH_CODE_PROVIDERS: readonly SocialProvider[] = ['google', 'facebook'];

function isOAuthCodeProvider(value: string): value is SocialProvider {
  return (OAUTH_CODE_PROVIDERS as readonly string[]).includes(value);
}

export default function SocialCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const providerRaw = searchParams.get('provider') ?? 'google';
  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const isPromotionAgreed = searchParams.get('isPromotionAgreed') === 'true';
  const idTokenParam =
    searchParams.get('id_token')?.trim() || searchParams.get('identity_token')?.trim();

  useEffect(() => {
    if (error) {
      setErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
      setIsLoading(false);
      return;
    }

    if (
      (!code || !isOAuthCodeProvider(providerRaw)) &&
      !(providerRaw === 'apple' && idTokenParam)
    ) {
      setErrorMessage(t('requestVerificationCodePage.socialLoginUnavailableMessage'));
      setIsLoading(false);
      return;
    }

    socialLogin({
      provider: providerRaw as SocialProvider,
      ...(code ? { authorizationCode: code } : {}),
      ...(idTokenParam ? { idToken: idTokenParam } : {}),
      isPromotionAgreed,
      ...(providerRaw === 'facebook' || providerRaw === 'google'
        ? { redirectUri: getGoogleRedirectUri() }
        : {})
    })
      .then((response) => {
        if (isSocialLoginSuccess(response)) {
          completeAuthLogin({ response, navigate });
          return;
        }
        if (shouldRequireEmailVerification(response)) {
          const socialSessionToken = getSocialSessionToken(response);
          const candidateEmails = getSocialCandidateEmails(response);
          if (!socialSessionToken || candidateEmails.length === 0) {
            setErrorMessage(t('requestVerificationCodePage.socialCandidateEmailsMissingMessage'));
            return;
          }
          navigate('/social-account-selection?init=true', {
            state: {
              provider: providerRaw,
              socialSessionToken,
              candidateEmails,
              continueWithNewAccountAvailable: canContinueWithNewSocialAccount(response),
              isPromotionAgreed
            }
          });
          return;
        }
        setErrorMessage(
          getSocialLoginErrorMessage(response) ??
            t('requestVerificationCodePage.socialLoginFailedMessage')
        );
      })
      .catch(() => {
        setErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [code, error, providerRaw, idTokenParam, isPromotionAgreed, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark px-6">
        <img src="/assets/icons/attention.png" alt="attention" className="w-[82px] h-[82px]" />
        <div className="px-[24px] py-[10px] flex flex-col items-center gap-[14px]">
          <p className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-center">
            {t('requestVerificationCodePage.errorTitle')}
          </p>

          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center">
            {errorMessage}
          </p>
        </div>
        <div className="fixed bottom-0 left-0 right-0 pb-[calc(env(safe-area-inset-bottom)+10px)] px-[24px]">
          <Button
            onClick={() => navigate('/onboarding', { replace: true })}
            size="large"
            className="w-full text-[17px] font-semibold"
          >
            {t('requestVerificationCodePage.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
